import { ReceptionServiceWithPractitioners } from '@dreryk/egabinet-private-medical-service-frontend-api';

import { createVisitor, isEmpty } from '~libs.typescript';

import { ReceptionServiceFormBean } from '../../ReceptionServiceForm';
import { WithAdvancePayment, WithoutAdvancePayment } from '../advancePayment/ReceptionServiceAdvancePayment.types';

import { AsFree, WithPrice, WithPriceRange } from './receptionServicePrice.types';

const isFreeChecked = (data: Pick<ReceptionServiceWithPractitioners, 'maxPrice' | 'minPrice'>): boolean => {
   const minPrice = data.minPrice.asNumber();
   const maxPrice = data.maxPrice.asNumber();
   return !minPrice && !maxPrice;
};

export const receptionServicePriceVisitor = (bean: ReceptionServiceFormBean) => <ACCEPT>(visitor: {
   asFree?: (bean: AsFree) => ACCEPT;
   withPrice?: (bean: WithPrice) => ACCEPT;
   withPriceRange?: (bean: WithPriceRange) => ACCEPT;
}) => {
   if (isEmpty(bean)) {
      return null;
   }

   if (bean.freeChecked) {
      return visitor.asFree?.(bean);
   }

   if (bean.priceRangeChecked) {
      return visitor.withPriceRange?.(bean);
   }

   return visitor.withPrice?.(bean);
};

export const receptionServicePaymentTypeVisitor = createVisitor<
   ReceptionServiceFormBean,
   {
      asFree: AsFree;
      withAdvancePayment: WithAdvancePayment;
      withoutAdvancePayment: WithoutAdvancePayment;
      notFree: WithAdvancePayment | WithoutAdvancePayment;
   }
>((data, visitor) => {
   if (data.freeChecked) {
      return visitor.asFree?.(data);
   }

   if (visitor.notFree) {
      return visitor.notFree(data);
   }

   if (data.advancePaymentChecked) {
      return visitor.withAdvancePayment?.(data);
   }

   return visitor.withoutAdvancePayment?.(data);
});

export const mapReceptionServiceAdvancePaymentToBean = (
   data: ReceptionServiceWithPractitioners
): AsFree | WithAdvancePayment | WithoutAdvancePayment => {
   if (isFreeChecked(data)) {
      return { freeChecked: true };
   }

   if (data.advancePayment) {
      return {
         freeChecked: false,
         advancePaymentChecked: true,
         advancePaymentAmount: data.advancePayment.value.asNumber(),
      };
   }

   return {
      freeChecked: false,
      advancePaymentChecked: false,
   };
};

export const mapReceptionServicePriceToBean = (
   data: Pick<ReceptionServiceWithPractitioners, 'maxPrice' | 'minPrice'>
): AsFree | WithPrice | WithPriceRange => {
   const minPrice = data.minPrice.asNumber();
   const maxPrice = data.maxPrice.asNumber();

   if (isFreeChecked(data)) {
      return { freeChecked: true };
   }

   if (minPrice !== maxPrice) {
      return {
         freeChecked: false,
         priceRangeChecked: true,
         priceRange: {
            min: minPrice,
            max: maxPrice,
         },
      };
   }

   return {
      freeChecked: false,
      priceRangeChecked: false,
      price: minPrice,
   };
};
